import { forwardRef, HTMLAttributes, useCallback, useRef } from 'react'
import { TransitionStatus } from 'react-transition-group'
import { useIsomorphicLayoutEffect } from 'react-use'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import Lenis from 'lenis'
import Link from 'next/link'
import { useRouter } from 'next/router'

import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { useStore } from '@/contexts/store'
import { useLayoutStore } from '@/store/layout'
import { useLocaleStore } from '@/store/locale'
import { stripSlash } from '@/utils/path'

import LanguageList from './language-list'
import { cn } from '@/lib/utils'
import { WPMenu } from '@/types'

export type MenuItemProps = HTMLAttributes<HTMLElement> & {
  menu?: WPMenu['menu']
  variant?: 'menu-sans' | 'menu-serif' | 'icon' | 'h4'
  dataName?: HTMLAttributes<HTMLAnchorElement>
}

export type MenuProps = HTMLAttributes<HTMLDivElement> & {
  state: TransitionStatus
  menuOpen: boolean
}

const Menu = forwardRef<HTMLDivElement, MenuProps>(({ state, menuOpen, ...props }, ref) => {
  const { menu } = useStore()
  const { asPath, defaultLocale } = useRouter()
  const wrapper = useRef<HTMLDivElement>(null!)
  const content = useRef<HTMLDivElement>(null!)
  const currentLocale = useLocaleStore((state) => state.currentLocale)
  const setLayoutState = useLayoutStore((state) => state.setState)

  const isActive = useCallback(
    (url: string) =>
      stripSlash(url) ===
      stripSlash(`${currentLocale !== defaultLocale ? `/${currentLocale}` : ''}${asPath}`),
    [asPath, defaultLocale, currentLocale]
  )

  useIsomorphicLayoutEffect(() => {
    const lenis = new Lenis({
      smoothWheel: true,
      wrapper: wrapper.current,
      content: content.current
    })

    const tick = (time: number) => {
      lenis.raf(time * 1000)
    }

    gsap.ticker.add(tick)

    return () => {
      gsap.ticker.remove(tick)
      lenis.destroy()
    }
  }, [])

  useGSAP(
    () => {
      if (!menuOpen) return
      const timeline = gsap.timeline({ delay: 0.3 })
      timeline.fromTo(
        ['[data-motion-link-menu]'],
        {
          opacity: 0,
          y: '-10%'
        },
        {
          opacity: 1,
          y: '0%',
          duration: 0.8,
          ease: 'power4.out',
          stagger: 0.05
        }
      )

      return () => {
        timeline.reverse()
      }
    },
    { scope: wrapper, dependencies: [menuOpen] }
  )

  /*------------------------------
    Handle Escape Key
    ------------------------------*/
  useIsomorphicLayoutEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setLayoutState({ menuOpen: false })
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [setLayoutState])

  return (
    <>
      <div
        ref={ref}
        className={cn(
          'rounded-0 pointer-events-auto fixed right-0 top-0 z-30 grid w-full shadow-md md:right-[5.6rem] md:top-[2.1rem] md:w-auto md:rounded-radius-minimal',
          'bg-white-light text-text-accent-light',
          'transition-all duration-700 ease-in-out-cubic',
          {
            'grid-rows-[0fr] duration-500': state === 'entering' || state === 'exiting'
          },
          { 'grid-rows-[1fr]': state === 'entered' }
        )}
        data-motion-background
        data-lenis-prevent
        {...props}
      >
        <button
          className={cn([
            'group absolute left-auto right-0 top-0 z-10 flex h-[6.6rem] w-[8.6rem] items-center justify-center md:left-0 md:right-auto',
            {
              'opacity-0': state === 'entering',
              'opacity-100 transition-opacity delay-300 duration-300 ease-in-out-cubic':
                state === 'entered',
              'opacity-0 transition-opacity duration-300 ease-in-out-cubic': state === 'exiting'
            }
          ])}
          onClick={() => setLayoutState({ menuOpen: false })}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            xmlns="http://www.w3.org/2000/svg"
            className="transition-scale fill-current duration-500 group-hover:scale-125"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M3.95956 3.95939C4.40587 3.51308 5.12949 3.51308 5.5758 3.95939L8.00017 6.38376L10.4245 3.95939C10.8708 3.51308 11.5945 3.51308 12.0408 3.95939C12.4871 4.4057 12.4871 5.12932 12.0408 5.57563L9.61641 8L12.0408 10.4244C12.4871 10.8707 12.4871 11.5943 12.0408 12.0406C11.5945 12.4869 10.8708 12.4869 10.4245 12.0406L8.00017 9.61624L5.5758 12.0406C5.12949 12.4869 4.40587 12.4869 3.95956 12.0406C3.51324 11.5943 3.51324 10.8707 3.95956 10.4244L6.38392 8L3.95956 5.57563C3.51324 5.12932 3.51324 4.4057 3.95956 3.95939Z"
            />
          </svg>
        </button>
        <div
          ref={wrapper}
          className={cn('relative z-[9] overflow-hidden md:max-h-[calc(100vh-4.0rem)]')}
          data-lenis-prevent
        >
          <div
            ref={content}
            className="h-svh-vh flex px-spacing-md pb-[2.4rem] pt-[12.0rem] md:h-auto"
            data-motion-link-menu
          >
            <div className="grid gap-y-spacing-2xl">
              <nav className="flex flex-col space-y-spacing-sm">
                {menu?.main_menu_01?.menu.map((item, index) => (
                  <Link
                    key={`main_menu_01_${item.id}_${index}`}
                    href={item.url}
                    scroll={false}
                    target={item.target}
                    className={cn('group inline-flex self-start', {
                      '!pointer-events-none opacity-40': isActive(item.url)
                    })}
                  >
                    <Typography variant="h6" mask>
                      {item.title}
                    </Typography>
                  </Link>
                ))}
              </nav>
              <nav className="flex flex-col space-y-spacing-xs">
                {menu?.main_menu_02?.menu.map((item, index) => (
                  <Link
                    key={`main_menu_02_${item.id}_${index}`}
                    href={item.url}
                    scroll={false}
                    target={item.target}
                    className={cn('group inline-flex self-start', {
                      '!pointer-events-none opacity-40': isActive(item.url)
                    })}
                  >
                    <Typography variant="large" mask>
                      {item.title}
                    </Typography>
                  </Link>
                ))}
              </nav>
              <div className="flex items-center justify-between">
                <nav className="flex gap-spacing-xs">
                  {menu?.social_menu?.menu.map((item, index) => (
                    <Link
                      key={`social_menu_${item.id}_${index}`}
                      href={item.url}
                      scroll={false}
                      target={item.target}
                    >
                      <SVGIcon
                        symbol={`${item.title.toLowerCase()}-icon`}
                        className="size-[1.6rem] fill-current"
                      />
                    </Link>
                  ))}
                </nav>
                <LanguageList />
              </div>
            </div>
          </div>
        </div>
      </div>
      <button
        className={cn([
          'h-svh-vh fixed left-0 top-0 z-20 hidden w-full cursor-default md:block',
          {
            'opacity-0': state === 'entering',
            'opacity-100 transition-opacity duration-300 ease-in-out-cubic': state === 'entered',
            'opacity-0 transition-opacity duration-300 ease-in-out-cubic': state === 'exiting'
          }
        ])}
        onClick={() => setLayoutState({ menuOpen: false })}
      ></button>
    </>
  )
})

export default Menu
